import { NextSeo } from 'next-seo';
import Head from 'next/head';

//screens
import { AI } from '@/screens/Latam/AI';

//data-helpers
import {
  JsonLd,
  Organization,
  SoftwareApplication,
} from '@/datafiles/micro-data';
import { buildFaqSchema } from '@/datafiles/micro-data/faqPage';
import { getLatamDictionary } from '@/datafiles/languages/latam';
import { buildLatamChrome } from '@/datafiles/nav/latam';
import { OgLocaleAlternates } from '@/components/OgLocaleAlternates';
import { getLocalizedSeo } from '@/datafiles/i18n/seo';
import { getPathFor } from '@/datafiles/i18n/translatedPages';
import { LOCALES } from '@/datafiles/i18n/locales';

// Task-card slug -> registry id of that agent's page (see pages.json).
const AGENT_PAGE_IDS = {
  document_collection_and_filing: 'latam-agent-document-collection',
  tracking_and_status_updates: 'latam-agent-tracking',
  tender_failures: 'latam-agent-tender-failures',
  proactive_load_audits: 'latam-agent-load-audits',
  claims_processing: 'latam-agent-claims',
  scheduling_and_re_scheduling: 'latam-agent-scheduling',
};

// One body for every LATAM AI route (/en/ai/, /pt-br/ia/, /es-mx/ia/,
// /es-co/ia/): each route file only picks the locale. Sections shared with the
// homepage (stats, outcomes, integrations diagram) come from `home`.
export function LatamAiPage({ locale }) {
  const { ai, home } = getLatamDictionary(locale);
  const { nav, footer } = buildLatamChrome(locale);
  const seo = getLocalizedSeo('latam-home', locale);
  const htmlLang = LOCALES[locale].htmlLang;

  const contactHref = `${getPathFor('latam-contact', locale, {
    includePlanned: true,
  })}#contact-form`;

  const liveTaskHrefs = Object.fromEntries(
    Object.entries(AGENT_PAGE_IDS).map(([slug, id]) => [
      slug,
      getPathFor(id, locale, { includePlanned: true }),
    ])
  );

  const hero_content = {
    title: ai.hero.title,
    titleHighlight: ai.hero.title_highlight,
    copy: ai.hero.copy,
    tagline: ai.hero.tagline,
    ctaLabel: ai.hero.cta_label,
    ctaHref: contactHref,
  };

  const live = ai.live_in_production;
  const liveInProduction_content = {
    kicker: live.kicker,
    title: live.title,
    titleHighlight: live.title_highlight,
    lede: live.lede,
    bigProof: live.big_proof,
    exceptionsBanner: live.exceptions_banner,
    tasks: live.tasks,
    customCard: {
      kicker: live.custom_card.kicker,
      title: live.custom_card.title,
      copy: live.custom_card.copy,
      ctaLabel: live.custom_card.cta_label,
    },
  };

  const platformScale_content = {
    label: home.platform_scale.label,
    stats: home.platform_scale.stats,
  };

  const st = ai.scenario_theater;
  const scenarioTheater_content = {
    title: st.title,
    statusWorking: st.status_working,
    statusResolved: st.status_resolved,
    summaryLabel: st.summary_label,
    sessionLabel: st.session_label,
    scenarios: st.scenarios,
  };

  const endCta_content = {
    title: ai.end_cta.title,
    titleHighlight: ai.end_cta.title_highlight,
    ctaLabel: ai.end_cta.cta_label,
    ctaHref: contactHref,
  };

  const aw = ai.agentic_workflow;
  const agenticWorkflow_content = {
    kicker: aw.kicker,
    title: aw.title,
    lede: aw.lede,
    podTitle: aw.pod_title,
    statusWorking: aw.status_working,
    statusResolved: aw.status_resolved,
    sessionLabel: aw.session_label,
    replayLabel: aw.replay_label,
    trueLabel: aw.true_label,
    falseLabel: aw.false_label,
    nodes: aw.nodes,
  };

  const diagram = home.rails.diagram;
  const rails_content = {
    kicker: ai.rails.kicker,
    title: ai.rails.title,
    titleHighlight: ai.rails.title_highlight,
    lede: ai.rails.lede,
    diagram: {
      ariaLabel: diagram.aria_label,
      systemsLabel: diagram.systems_label,
      freightWorldLabel: diagram.freight_world_label,
      apiLabel: diagram.api_label,
      ediLabel: diagram.edi_label,
      mcpLabel: diagram.mcp_label,
      tmsLabel: diagram.tms_label,
      erpWmsLabel: diagram.erp_wms_label,
      emailEdiLabel: diagram.email_edi_label,
      carriersLabel: diagram.carriers_label,
      facilitiesLabel: diagram.facilities_label,
    },
  };

  const proof_content = {
    kicker: home.proof.kicker,
    title: home.proof.title,
    outcomes: Object.values(home.proof.outcomes),
  };

  const faq_content = { title: home.faq.title, items: ai.faq.items };

  const softwareApplication = {
    ...SoftwareApplication,
    url: seo.canonical,
    inLanguage: htmlLang,
    description: ai.meta_description,
    featureList: Object.values(live.tasks).map((task) => task.title),
  };

  return (
    <>
      <Head>
        <meta name="keywords" content={ai.meta_keywords} />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG === 'true'}
        noindex={process.env.NOINDEX_FLAG === 'true'}
        title={ai.meta_title}
        description={ai.meta_description}
        canonical={seo.canonical}
        languageAlternates={seo.languageAlternates}
        openGraph={{
          url: seo.canonical,
          locale: seo.ogLocale,
          images: [
            {
              url: `${process.env.SITE_URL}/images/share/loadsmart-ai-card.png`,
              width: 1200,
              height: 630,
              alt: 'Loadsmart AI',
              type: 'image/png',
            },
          ],
        }}
      />
      <OgLocaleAlternates locales={seo.ogLocaleAlternates} />
      <JsonLd data={Organization} />
      <JsonLd data={softwareApplication} />
      <JsonLd data={buildFaqSchema(faq_content.items)} />
      <AI
        hero_content={hero_content}
        liveInProduction_content={liveInProduction_content}
        platformScale_content={platformScale_content}
        scenarioTheater_content={scenarioTheater_content}
        endCta_content={endCta_content}
        agenticWorkflow_content={agenticWorkflow_content}
        rails_content={rails_content}
        proof_content={proof_content}
        faq_content={faq_content}
        liveTaskHrefs={liveTaskHrefs}
        numberLocale={htmlLang}
        nav={nav}
        footer={footer}
      />
    </>
  );
}
